import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { AnnualAccountsData } from '../../types/inpi';
import { formatDate } from '../../utils/inseeCodeMappings';
import inpiService from '../../services/inpiService';

interface AnnualAccountsCardProps {
  annualAccounts: AnnualAccountsData | null;
  isLoading: boolean;
  error: string | null;
  onPageChange: (page: number) => void;
}

const AnnualAccountsCard: React.FC<AnnualAccountsCardProps> = ({ 
  annualAccounts, 
  isLoading, 
  error,
  onPageChange
}) => {
  if (error) {
    return (
      <div>
        <p className="text-sm text-gray-600">Données temporairement indisponibles</p>
      </div>
    );
  }

  const hasAccounts = annualAccounts?.items && annualAccounts.items.length > 0;
  const currentPage = annualAccounts?.pagination?.page || 1;
  const totalPages = annualAccounts?.pagination?.total_pages || 1;

  // Loading skeleton for table rows
  const renderTableRowsSkeleton = () => (
    Array.from({ length: 5 }).map((_, index) => (
      <tr key={index}>
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton width={100} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton width={100} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton width={100} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton width={80} />
        </td>
      </tr>
    ))
  );

  return (
    <div>      
      {isLoading ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de clôture
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de dépôt
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidentialité
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {renderTableRowsSkeleton()}
            </tbody>
          </table>
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-700">
              <Skeleton width={80} />
            </div>
            <div className="flex space-x-2">
              <Skeleton width={80} height={32} className="rounded" />
              <Skeleton width={80} height={32} className="rounded" />
            </div>
          </div>
        </div>
      ) : hasAccounts ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de clôture
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de dépôt
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidentialité
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {annualAccounts?.items?.map((account) => (
                  <tr key={account.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.date_cloture ? formatDate(account.date_cloture) : 'Non renseigné'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.date_depot ? formatDate(account.date_depot) : 'Non renseigné'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.confidentialite || 'Non renseigné'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <a 
                        href={inpiService.getDocumentDownloadUrl(account.id, 'compte_annuel')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Télécharger
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded ${
                    currentPage === 1
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Précédent
                </button>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded ${
                    currentPage === totalPages
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-600">Aucun compte annuel disponible</p>
      )}
    </div>
  );
};

export default AnnualAccountsCard; 